/**
 * Translation, in about a hundred lines and with no dependency.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS AS A LAYER
 * ---------------------------------------------------------------------------
 *
 * This product goes to more than one park, and parks are not all in one
 * country. A Dutch sentence typed straight into a component is portable right
 * up until the moment it has to be anything else — at which point every screen
 * has to be reopened and every sentence found again. Doing that at ~250 strings
 * is an afternoon; doing it at thousands is a project nobody funds.
 *
 * So ENGLISH IS THE SOURCE and Dutch is a first-class catalogue, not a
 * fallback. `locales/en.ts` defines the shape, `locales/nl.ts` must satisfy it,
 * and a key Dutch has not translated fails `tsc` rather than surfacing as
 * English text in front of a Dutch operator — the failure mode that makes a
 * half-done translation invisible to the only person who would notice.
 *
 * ---------------------------------------------------------------------------
 * ENGLISH NEVER TOUCHES DUTCH
 * ---------------------------------------------------------------------------
 *
 * English resolves out of the statically imported catalogue and stops there.
 * Every other language is fetched by dynamic `import()` on demand, so an
 * English session never loads, parses or merges Dutch, and a build for a park
 * in another country carries no Dutch at all — Vite splits each catalogue into
 * its own chunk.
 *
 * Degradation, if it ever happens at all, runs ONE WAY: a translated catalogue
 * may fall through to English, and English never reaches for a translation.
 * The two directions are not comparable. An English sentence in a Dutch screen
 * is untidy and every Dutch operator can still read it; a Dutch sentence in an
 * English screen is unreadable to the person looking at it, and there is no
 * graceful version of that. Because English is the source and complete by
 * construction, the safe direction is also the only one the code can take.
 *
 * Adding a third language is: write the file, add a loader line, add it to
 * `LANGUAGES`.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT A LIBRARY
 * ---------------------------------------------------------------------------
 *
 * The whole feature is a map lookup, a `{name}` substitution and a
 * one-or-other choice. i18next is tens of kilobytes to do that, plus a plugin
 * chain, plus a loader story for catalogues that a bundler already handles.
 * This repo has almost no dependencies by choice; a translation library is not
 * the one to start with.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS NOT TRANSLATED
 * ---------------------------------------------------------------------------
 *
 * Data. Period labels, accommodation names, version and rate labels are what
 * the operator typed into the database, and a catalogue that rewrote them would
 * be showing them something they never wrote. They pass through as values.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { en, type Catalogue, type MessageKey } from '../locales/en';
import {
  applyScheme, browserLanguages, readChoice, readScheme, resolveLanguage, writeChoice,
  writeScheme, type Scheme,
} from '@shared/index';

export type { Catalogue, MessageKey } from '../locales/en';

export type Lang = 'nl' | 'en';

/** Whatever a message interpolates: counts, names, dates already formatted. */
export type Values = Record<string, string | number>;

/**
 * How each language arrives.
 *
 * English is already here — it is the source, and the type every other
 * catalogue is checked against. The rest are `import()` calls, which is what
 * keeps them out of an English bundle.
 */
const LOADERS: Record<Lang, () => Promise<Catalogue>> = {
  en: () => Promise.resolve(en),
  nl: () => import('../locales/nl').then((m) => m.nl),
};

/** Loaded catalogues, module-level so switching back is instant. */
const loaded = new Map<Lang, Catalogue>([['en', en]]);

/**
 * Language names in their own language, never translated.
 *
 * A switcher exists for someone who cannot read the language currently on
 * screen; "Dutch" is no use to the operator who needs it, and "Nederlands" is
 * legible whatever the interface happens to be showing. Endonyms are a property
 * of the language, not copy about the product, so they live here rather than in
 * any catalogue.
 */
/** Keyed, for naming what `auto` currently resolves to. */
export const LANGUAGE_NAMES: Record<Lang, string> = { nl: 'Nederlands', en: 'English' };

export const LANGUAGES: ReadonlyArray<readonly [Lang, string]> = [
  ['nl', 'Nederlands'],
  ['en', 'English'],
];

/**
 * The browser decides, not us.
 *
 * This used to be `nl`, hardcoded, reasoning that the first park is Dutch and
 * defaulting to English would mean everyone changing a setting on every new
 * machine. True of that park and wrong for every other one — and the browser has
 * already been told what its owner reads, so there is nothing to guess.
 *
 * `SOURCE_LANG` is the floor for a browser asking for a language this build does
 * not have. English, because it is the source: a reader meeting it is meeting the
 * original rather than a translation of unknown age.
 */
const SOURCE_LANG: Lang = 'en';
const SUPPORTED: readonly Lang[] = ['en', 'nl'];

const STORAGE_KEY = 'vergarde.lang';
const SCHEME_KEY = 'vergarde.scheme';

export type TFunction = (key: MessageKey, values?: Values) => string;

interface I18nApi {
  lang: Lang;
  setLang: (lang: Lang | 'auto') => void;
  /** False when `lang` came from the browser rather than from a choice. */
  explicit: boolean;
  t: TFunction;
}

const I18nContext = createContext<I18nApi | null>(null);

/**
 * The language to open in: an explicit choice, else the browser, else English.
 *
 * The policy itself lives in `@shared/language` and is the same one the guest
 * site, the embed and the WordPress plugin follow. Two implementations of "which
 * language does this render in" is two answers, and the one nobody edited is the
 * one a park notices.
 */
function startingLang(): Lang {
  return resolveLanguage<Lang>({
    supported: SUPPORTED,
    fallback: SOURCE_LANG,
    chosen: readChoice<Lang>(STORAGE_KEY, SUPPORTED),
    browser: browserLanguages(),
  });
}

/**
 * `{name}` substitution.
 *
 * An unknown placeholder is left standing rather than replaced with the word
 * `undefined`, because a visible `{count}` is a bug report and "undefined" in
 * the middle of a sentence is a mystery.
 */
export function interpolate(text: string, values?: Values): string {
  if (!values) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (
    name in values ? String(values[name]) : whole
  ));
}

/**
 * Look a message up and fill it in.
 *
 * Plurals are one-or-other, chosen on `count`. Both languages here happen to
 * split the same way, and inventing a CLDR plural-rule engine for two Germanic
 * languages would be building for a case that does not exist yet. A language
 * needing more forms needs a wider `Plural` shape — and the compiler will then
 * say so at every catalogue at once, which is the point.
 */
export function translate(cat: Catalogue, key: MessageKey, values?: Values): string {
  // `?? en[key]` is the one-directional safety net, and it is a net rather than
  // a strategy: `Catalogue` makes a missing Dutch key a compile error, so this
  // branch is unreachable from a catalogue that type-checked. It exists so that
  // one absent key degrades to an English word instead of throwing the whole
  // dashboard away, and it can only ever run in that direction — English IS
  // `en`, so English resolving through here resolves against itself.
  const message = cat[key] ?? en[key];
  // A key in NEITHER catalogue renders as itself.
  //
  // Unreachable through the type system, and reachable at runtime: a key built
  // from data (`inquiries.status${x}`), a catalogue chunk from an older build, a
  // hand-written string that got through an `as MessageKey`. `message` was then
  // undefined and `message.one` threw — one bad key took the whole screen down
  // rather than one label.
  //
  // The key itself is the right thing to show. It is visible, it names the fault,
  // and it is exactly what somebody needs to grep for. An empty string would hide
  // it, and "undefined" in the middle of a sentence is a mystery.
  if (message === undefined || message === null) return key;
  const text = typeof message === 'string'
    ? message
    : (Number(values?.count) === 1 ? message.one : message.other);
  return interpolate(text, values);
}

export function I18nProvider({ children, lang: forced }: {
  children: ReactNode;
  /** Pin the language, for a test or a screenshot. */
  lang?: Lang;
}) {
  const [chosen, setChosen] = useState<Lang>(startingLang);
  // Whether the operator has actually PICKED one, as opposed to being shown
  // whatever their browser asks for. Kept apart from `lang` so the switcher can
  // offer "follow the browser" as somewhere to go back to — a stored language is
  // otherwise a decision nobody can undo, on that machine, forever.
  const [explicit, setExplicit] = useState<boolean>(
    () => readChoice<Lang>(STORAGE_KEY, SUPPORTED) !== null,
  );
  const [, bump] = useState(0);
  const lang = forced ?? chosen;

  /**
   * Load first, switch second.
   *
   * This used to set the language immediately and render `null` for the tick
   * before the catalogue arrived. That is not a flicker — React UNMOUNTS the
   * children, and every piece of state in them goes with it: the open tab, the
   * selected version, a price half typed into a cell. An operator who changed
   * language while working lost the work, and the screen came back looking like a
   * fresh login.
   *
   * Waiting for the chunk fixes it outright rather than trading one fault for
   * another. There is no moment with a new language and no words for it, so there
   * is nothing to hide and nothing to unmount, and the "no frame of the wrong
   * language" property the old code was protecting still holds.
   *
   * The choice is stored only once the catalogue is really there. A language
   * persisted from a load that failed would be a dashboard that comes back blank
   * on every visit, with the setting that breaks it invisible.
   */
  const setLang = useCallback((next: Lang | 'auto') => {
    // `auto` stores the ABSENCE of a choice rather than the language it happens
    // to resolve to today. An operator who moves, or whose browser changes, then
    // follows it — which is what they asked for by choosing auto.
    if (next === 'auto') {
      writeChoice(STORAGE_KEY, 'auto');
      setExplicit(false);
      const resolved: Lang = resolveLanguage<Lang>({
        supported: SUPPORTED, fallback: SOURCE_LANG, browser: browserLanguages(),
      });
      if (loaded.has(resolved)) { setChosen(resolved); return; }
      void LOADERS[resolved]().then((cat: Catalogue) => {
        loaded.set(resolved, cat);
        setChosen(resolved);
      });
      return;
    }
    const commit = () => {
      setChosen(next);
      setExplicit(true);
      writeChoice(STORAGE_KEY, next);
    };
    if (loaded.has(next)) { commit(); return; }
    void LOADERS[next]().then((cat) => { loaded.set(next, cat); commit(); });
  }, []);

  /*
   * Fetch the catalogue for the language we START in — from storage, or pinned by
   * a test. `setLang` handles every later change, so this runs once in practice.
   */
  useEffect(() => {
    if (loaded.has(lang)) return undefined;
    let cancelled = false;
    void LOADERS[lang]().then((cat) => {
      loaded.set(lang, cat);
      if (!cancelled) bump((n) => n + 1);
    });
    return () => { cancelled = true; };
  }, [lang]);

  // Read through the cache during render rather than mirroring it into state:
  // a state copy lags one render behind, and that render is a frame of the
  // WRONG language on screen — precisely the English-shows-through that the
  // absence of a fallback is meant to rule out.
  const catalogue = loaded.get(lang) ?? null;

  const api = useMemo<I18nApi | null>(() => (catalogue ? {
    lang,
    setLang,
    // A pinned language is a decision by whoever pinned it, so the switcher shows
    // it as chosen rather than as following anything.
    explicit: forced ? true : explicit,
    t: (key, values) => translate(catalogue, key, values),
  } : null), [catalogue, lang, setLang, explicit, forced]);

  // Only reachable on the FIRST paint, before the starting catalogue has landed —
  // when there are no children yet and so no state to lose. A language change no
  // longer arrives here at all; see `setLang`.
  if (!api) return null;

  return <I18nContext.Provider value={api}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nApi {
  const api = useContext(I18nContext);
  // Loud rather than silently English: a screen rendered outside the provider
  // would otherwise look translated to whoever wrote it and to nobody else.
  if (!api) throw new Error('useI18n outside an I18nProvider');
  return api;
}

/** The lookup function. `const t = useT()` then `t('matrix.save')`. */
export function useT(): TFunction {
  return useI18n().t;
}

/**
 * A message whose emphasis is part of the sentence.
 *
 * Splitting "**Beyond included** charges only the people past the included
 * count" into a bold fragment and a plain one would hand a translator two
 * halves of a sentence and no way to move the emphasis, which in Dutch lands on
 * a different word. So the marker travels inside the message — `*like this*` —
 * and each catalogue decides for itself what it wraps.
 */
export function Rich({ id, values, mark = 'strong' }: {
  id: MessageKey;
  values?: Values;
  /** `kbd` for a key name, `strong` for emphasis. */
  mark?: 'strong' | 'kbd';
}) {
  const t = useT();
  const Mark = mark;
  return (
    <>
      {t(id, values).split(/\*([^*]+)\*/).map((part, i) => (
        // Odd indices are what the capture group caught: the marked runs.
        i % 2 === 0 ? part : <Mark key={`${i}-${part}`}>{part}</Mark>
      ))}
    </>
  );
}

/**
 * The switcher.
 *
 * A select, in the header, next to the season. This is the operator dashboard
 * and not the guest site: flags are ambiguous (which flag is English?) and a
 * modal picker for two options is theatre.
 */
export function LanguageSwitcher() {
  const { lang, setLang, explicit, t } = useI18n();
  return (
    <div className="field" style={{ maxWidth: 150 }}>
      <label htmlFor="lang">{t('app.language')}</label>
      <select
        id="lang"
        // `auto` when nothing has been chosen, so the control shows the truth: the
        // language on screen came from the browser and will keep following it.
        value={explicit ? lang : 'auto'}
        onChange={(e) => setLang(e.target.value as Lang | 'auto')}
      >
        {/* First, because it is the default and the one to come back to. Named
            with the language it currently resolves to, so choosing it is not a
            step into the dark. */}
        <option value="auto">{t('app.languageAuto', { lang: LANGUAGE_NAMES[lang] })}</option>
        {LANGUAGES.map(([id, label]) => (
          <option key={id} value={id}>{label}</option>
        ))}
      </select>
    </div>
  );
}

/**
 * Light, dark, or whatever the device says.
 *
 * The stylesheet has carried three states since it was written — a complete light
 * palette on bare `:root`, a dark one under `prefers-color-scheme` guarded by
 * `:not([data-theme="light"])`, and a third block so an explicit choice wins in
 * both directions. Nothing ever stamped the attribute, so the automatic branch
 * worked and the two deliberate ones were unreachable.
 *
 * `system` is the default and is stored as itself, because it is a real answer
 * rather than the absence of one: an operator who wants this dashboard to follow
 * their machine has said something, and it must survive a reload.
 */
export function SchemeSwitcher() {
  const t = useT();
  const [scheme, setScheme] = useState<Scheme>(() => readScheme(SCHEME_KEY));

  // During the effect rather than at module load: the attribute belongs to the
  // page, and a component that stamps it while rendering would fight a second
  // copy of itself in a test.
  useEffect(() => { applyScheme(scheme); }, [scheme]);

  const choose = (next: Scheme) => {
    setScheme(next);
    writeScheme(SCHEME_KEY, next);
  };

  return (
    <div className="field" style={{ maxWidth: 130 }}>
      <label htmlFor="scheme">{t('app.scheme')}</label>
      <select id="scheme" value={scheme} onChange={(e) => choose(e.target.value as Scheme)}>
        <option value="system">{t('app.schemeSystem')}</option>
        <option value="light">{t('app.schemeLight')}</option>
        <option value="dark">{t('app.schemeDark')}</option>
      </select>
    </div>
  );
}
